import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare,
  Send,
  Loader2,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Sparkles,
  ShieldCheck,
  ShieldOff,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import {
  usePatientSmsLogs,
  usePatientSmsLogsRealtime,
  useSendSms,
  useSmsClinic,
  type SmsLogStatus,
} from '@/hooks/useSms';
import { usePatient, useUpdatePatient } from '@/hooks/usePatients';
import type { Database } from '@/types/database.generated';

interface SmsTabProps {
  clinicId: string;
  patientId: string;
}

type SmsConsentStatus = Database['public']['Enums']['sms_consent_status'];
type SmsConsent = {
  status?: SmsConsentStatus;
  updated_at?: string;
  source?: string;
};

const DEFAULT_PRICE_DKK_CENTS = 25; // 0.25 DKK per segment

function formatTime(ts: string | null | undefined): string {
  if (!ts) return '—';
  const d = new Date(ts);
  const day = d.toLocaleDateString('da-DK', { day: '2-digit', month: 'short' });
  const time = d.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
  return `${day} kl. ${time}`;
}

function statusPill(status: SmsLogStatus | null | undefined) {
  const { t } = useTranslation();
  switch (status) {
    case 'delivered':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-950/30 ring-1 ring-emerald-200/70 dark:ring-emerald-900 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
          <CheckCircle2 className="h-3 w-3" />
          {t('sms.status_delivered', 'Leveret')}
        </span>
      );
    case 'sent':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 dark:bg-sky-950/30 ring-1 ring-sky-200/70 dark:ring-sky-900 px-2 py-0.5 text-[11px] font-medium text-sky-700 dark:text-sky-300">
          <Clock className="h-3 w-3" />
          {t('sms.status_sent', 'Sendt')}
        </span>
      );
    case 'queued':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-950/30 ring-1 ring-amber-200/70 dark:ring-amber-900 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t('sms.status_queued', 'I kø')}
        </span>
      );
    case 'failed':
    case 'rejected':
    case 'expired':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 dark:bg-rose-950/30 ring-1 ring-rose-200/70 dark:ring-rose-900 px-2 py-0.5 text-[11px] font-medium text-rose-700 dark:text-rose-300">
          <AlertTriangle className="h-3 w-3" />
          {t('sms.status_failed', 'Fejlet')}
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 ring-1 ring-gray-200/70 dark:ring-gray-700 px-2 py-0.5 text-[11px] text-gray-500 dark:text-gray-400">
          —
        </span>
      );
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function triggerLabel(trigger: string | null | undefined, t: any): string {
  const translate = t;
  switch (trigger) {
    case 'manual':
      return translate('sms.trigger_manual', 'Manuel');
    case 'reminder_3d':
      return translate('sms.trigger_reminder_3d', 'Påmindelse · 3 dage');
    case 'reminder_1d':
      return translate('sms.trigger_reminder_1d', 'Påmindelse · 1 dag');
    case 'reminder_2h':
      return translate('sms.trigger_reminder_2h', 'Påmindelse · 2 timer');
    case 'reminder_custom':
      return translate('sms.trigger_reminder_custom', 'Påmindelse');
    case 'followup_1d':
      return translate('sms.trigger_followup_1d', 'Opfølgning · 1 dag');
    case 'followup_1w':
      return translate('sms.trigger_followup_1w', 'Opfølgning · 1 uge');
    case 'followup_1m':
      return translate('sms.trigger_followup_1m', 'Opfølgning · 1 måned');
    case 'followup_custom':
      return translate('sms.trigger_followup_custom', 'Opfølgning');
    case 'confirmation_booked':
      return translate('sms.trigger_confirmation_booked', 'Bekræftelse · Booket');
    case 'confirmation_cancelled':
      return translate('sms.trigger_confirmation_cancelled', 'Bekræftelse · Aflyst');
    case 'confirmation_rescheduled':
      return translate('sms.trigger_confirmation_rescheduled', 'Bekræftelse · Flyttet');
    case 'consent_request':
      return translate('sms.trigger_consent_request', 'Samtykke-anmodning');
    case 'test':
      return translate('sms.trigger_test', 'Test');
    default:
      return trigger || '—';
  }
}

function ConsentBadge({ status }: { status: SmsConsentStatus | undefined }) {
  const { t } = useTranslation();
  switch (status) {
    case 'accepted':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50/80 dark:bg-emerald-950/30 ring-1 ring-emerald-200/80 dark:ring-emerald-900 px-3 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
          <ShieldCheck className="h-3 w-3" />
          {t('sms.consent_accepted', 'Tilmeldt SMS')}
        </span>
      );
    case 'revoked':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 dark:bg-gray-800 ring-1 ring-gray-200/80 dark:ring-gray-700 px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-300">
          <ShieldOff className="h-3 w-3" />
          {t('sms.consent_revoked', 'Tilbagekaldt')}
        </span>
      );
    case 'declined':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50/80 dark:bg-rose-950/30 ring-1 ring-rose-200/80 dark:ring-rose-900 px-3 py-1 text-xs font-medium text-rose-700 dark:text-rose-300">
          <ShieldOff className="h-3 w-3" />
          {t('sms.consent_declined', 'Afvist')}
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50/80 dark:bg-amber-950/30 ring-1 ring-amber-200/80 dark:ring-amber-900 px-3 py-1 text-xs font-medium text-amber-700 dark:text-amber-300">
          <ShieldAlert className="h-3 w-3" />
          {t('sms.consent_not_set', 'Ikke besluttet')}
        </span>
      );
  }
}

export function SmsTab({ clinicId, patientId }: SmsTabProps) {
  const { t } = useTranslation();
  const [composeOpen, setComposeOpen] = useState(false);
  const [body, setBody] = useState('');

  const { data: patient, isLoading: patientLoading } = usePatient(patientId);
  const { data: clinic } = useSmsClinic(clinicId);
  const { data: logs = [], isLoading: logsLoading } = usePatientSmsLogs(clinicId, patientId);
  const sendSms = useSendSms();
  const updatePatient = useUpdatePatient();

  usePatientSmsLogsRealtime(clinicId, patientId);

  const consent = (patient?.sms_consent ?? {}) as SmsConsent;
  const consentStatus = consent.status ?? 'not_set';
  const phone = patient?.phone ?? '';
  const smsEnabled = clinic?.settings?.enabled ?? false;

  const segments = useMemo(() => {
    const len = body.length;
    if (len === 0) return 0;
    return len <= 160 ? 1 : Math.ceil(len / 153);
  }, [body]);

  const updateConsent = async (next: SmsConsentStatus) => {
    try {
      await updatePatient.mutateAsync({
        patient_id: patientId,
        sms_consent: {
          status: next,
          updated_at: new Date().toISOString(),
          source: 'clinic_admin',
        },
      });
      toast.success(
        next === 'accepted'
          ? t('sms.consent_updated_accepted', 'Patienten er tilmeldt SMS-kommunikation.')
          : next === 'revoked'
            ? t('sms.consent_updated_revoked', 'SMS-samtykke er trukket tilbage.')
            : t('sms.consent_updated_declined', 'SMS-samtykke er afvist.'),
      );
    } catch (err: any) {
      toast.error(err?.message || t('sms.consent_update_failed', 'Kunne ikke opdatere samtykke.'));
    }
  };

  const handleSend = async () => {
    if (!body.trim()) {
      toast.error(t('sms.body_required', 'SMS-tekst er påkrævet.'));
      return;
    }
    if (!phone) {
      toast.error(t('sms.no_phone', 'Patienten har intet telefonnummer.'));
      return;
    }

    try {
      await sendSms.mutateAsync({
        clinic_id: clinicId,
        patient_id: patientId,
        to: phone,
        body,
        trigger: 'manual',
        patient_name: patient ? `${patient.first_name} ${patient.last_name}` : undefined,
      });
      toast.success(t('sms.send_success', 'SMS sendt.'));
      setBody('');
      setComposeOpen(false);
    } catch (err: any) {
      toast.error(err?.message || t('sms.send_failed', 'SMS-afsendelse fejlede.'));
    }
  };

  if (patientLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!smsEnabled) {
    return (
      <section className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-card-dark">
        <div className="rounded-2xl bg-amber-50/70 dark:bg-amber-950/20 ring-1 ring-amber-200/80 dark:ring-amber-900 p-6 text-sm text-amber-800 dark:text-amber-200 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">
              {t('sms.not_enabled_title', 'SMS er ikke aktiveret for denne klinik')}
            </div>
            <div className="text-xs mt-1 text-amber-700 dark:text-amber-300">
              {t(
                'sms.not_enabled_hint',
                'Du kan aktivere SMS under Indstillinger → Integrationer → SMS.',
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-6">
      {/* Header with phone, consent, and compose */}
      <div className="rounded-2xl bg-white/80 dark:bg-gray-900/60 backdrop-blur ring-1 ring-gray-200/70 dark:ring-gray-800/70 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center justify-center h-10 w-10 rounded-2xl bg-indigo-600/10 ring-1 ring-indigo-600/20 text-indigo-600 dark:text-indigo-300 flex-shrink-0">
              <MessageSquare className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {t('sms.history', 'SMS-historik')}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {phone ? (
                  phone
                ) : (
                  <span className="text-rose-600">
                    {t('sms.no_phone_registered', 'Intet telefonnummer registreret')}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <ConsentBadge status={consentStatus} />
            {consentStatus !== 'accepted' && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => updateConsent('accepted')}
                disabled={updatePatient.isPending || !phone}
              >
                {updatePatient.isPending ? (
                  <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                ) : (
                  <ShieldCheck className="mr-1.5 h-3 w-3" />
                )}
                {t('sms.mark_as_accepted', 'Marker som tilmeldt')}
              </Button>
            )}
            {consentStatus === 'accepted' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateConsent('revoked')}
                disabled={updatePatient.isPending}
              >
                {updatePatient.isPending ? (
                  <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                ) : (
                  <ShieldOff className="mr-1.5 h-3 w-3" />
                )}
                {t('sms.revoke_consent', 'Træk samtykke tilbage')}
              </Button>
            )}
            <Button
              onClick={() => setComposeOpen((o) => !o)}
              disabled={consentStatus !== 'accepted' || !phone}
              size="sm"
            >
              <Send className="mr-1.5 h-3 w-3" />
              {composeOpen ? t('common.cancel', 'Annullér') : t('sms.send_sms', 'Send SMS')}
            </Button>
          </div>
        </div>

        {composeOpen && consentStatus === 'accepted' && (
          <div className="mt-4 space-y-3">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t('sms.compose_placeholder', 'Skriv en SMS til patienten…')}
              rows={3}
              maxLength={1000}
            />
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <Sparkles className="h-3 w-3" />
                <span>
                  {body.length} {t('sms.chars', 'tegn')} · {segments}{' '}
                  {segments === 1 ? t('sms.segment', 'segment') : t('sms.segments', 'segmenter')}{' '}
                  · {((segments * DEFAULT_PRICE_DKK_CENTS) / 100).toFixed(2)} DKK
                </span>
              </div>
              <Button onClick={handleSend} disabled={sendSms.isPending || !body.trim()} size="sm">
                {sendSms.isPending ? (
                  <>
                    <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                    {t('sms.sending', 'Sender…')}
                  </>
                ) : (
                  <>
                    <Send className="mr-1.5 h-3 w-3" />
                    {t('sms.send_now', 'Send nu')}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Log list */}
      <div className="rounded-2xl bg-white/80 dark:bg-gray-900/60 backdrop-blur ring-1 ring-gray-200/70 dark:ring-gray-800/70">
        {logsLoading ? (
          <div className="p-12 text-center text-sm text-gray-500 dark:text-gray-400 inline-flex items-center justify-center gap-2 w-full">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('sms.loading', 'Indlæser SMS-historik…')}
          </div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center">
            <MessageSquare className="h-8 w-8 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('sms.empty', 'Ingen SMS endnu')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('sms.empty_hint', 'Når du sender den første SMS, dukker den op her.')}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100/80 dark:divide-gray-800/60">
            {logs.map((log) => (
              <li key={log.id} className="px-5 py-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {statusPill(log.status)}
                      <span className="text-[11px] text-gray-500 dark:text-gray-400">
                        {triggerLabel(log.trigger, t)}
                      </span>
                      <span className="text-[11px] text-gray-400">→ {log.to_phone || '—'}</span>
                    </div>
                    <div className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap break-words">
                      {log.body}
                    </div>
                    {log.error_message && (
                      <div className="mt-1 text-[11px] text-rose-600 dark:text-rose-400">
                        {log.error_message}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-[11px] text-gray-500 dark:text-gray-400 flex-shrink-0 space-y-0.5">
                    <div>{formatTime(log.created_at)}</div>
                    <div className="font-mono">
                      {log.parts ?? 1} seg ·{' '}
                      {((log.clinic_price_cents ?? (log.parts ?? 1) * DEFAULT_PRICE_DKK_CENTS) / 100).toFixed(2)}{' '}
                      DKK
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
